import React, { useEffect, lazy, Suspense } from 'react';
import SEO from './components/SEO';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import GenerationBanner from './components/GenerationBanner';
import { pushEvent, initDataLayer } from './utils/dataLayer';
import { trackPageView } from './services/tracker';
import { captureAffiliateRef } from './utils/affiliateRef';
import Homepage from "./components/Homepage";
// Globally-rendered popups + chat. They're not on the critical render
// path — exit-intent + login-offer fire on user actions, cookie consent
// is only first-visit, support chat is a deferred widget. Lazy-loading
// keeps them out of the entry bundle (Lighthouse: 152 KiB unused JS).
const AuthPromotions = lazy(() => import('./components/AuthPromotions'));
const SupportContact = lazy(() => import('./components/SupportContact'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));

// Lazy-loaded route components for code splitting. Anything not on the
// critical homepage (`/`) path goes here — eager imports were inflating
// main.js and hurting mobile LCP/FCP.
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminPush = lazy(() => import('./components/AdminPush'));
const PushSubscribeBanner = lazy(() => import('./components/PushSubscribeBanner'));
const EmailVerifyBanner = lazy(() => import('./components/EmailVerifyBanner'));
const BookEditor = lazy(() => import('./components/BookEditor'));
const MyBooks = lazy(() => import('./components/MyBooks'));
const Library = lazy(() => import('./components/Library'));
const Features = lazy(() => import('./components/Features'));
const PublicBookReader = lazy(() => import('./components/PublicBookReader'));
const AIHumanizer = lazy(() => import('./components/AIHumanizer'));
const BookTranslator = lazy(() => import('./components/BookTranslator'));
const TranslateCompare = lazy(() => import('./components/TranslateCompare'));
const ChapterBuilder = lazy(() => import('./components/ChapterBuilder'));
const BookStart = lazy(() => import('./components/BookStart'));
const BookSignal = lazy(() => import('./components/BookSignal'));
const Affiliate = lazy(() => import('./components/Affiliate'));
const Partner = lazy(() => import('./components/Partner'));
const ReferralPage = lazy(() => import('./components/ReferralPage'));
const Account = lazy(() => import('./components/Account'));
const Subscription = lazy(() => import('./components/Subscription'));
const Checkout = lazy(() => import('./components/Checkout'));
const OfferPage = lazy(() => import('./components/OfferPage'));
const ThankYouPage = lazy(() => import('./components/ThankYouPage'));
const DynamicLandingPage = lazy(() => import('./components/DynamicLandingPage'));
const About = lazy(() => import('./components/About'));
// Newly-lazy (were eagerly imported, inflating main.js):
const BookGenerator = lazy(() => import('./components/BookGenerator'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Privacy = lazy(() => import('./components/Privacy'));
const Terms = lazy(() => import('./components/Terms'));
const Contact = lazy(() => import('./components/Contact'));
const VerifyEmail = lazy(() => import('./components/VerifyEmail'));
const CheckEmail = lazy(() => import('./components/CheckEmail'));
const StartLanding = lazy(() => import('./components/StartLanding'));

// Loading fallback for code-split routes
const LoadingFallback = () => (
  <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:'60vh'}}>
    <div style={{textAlign:'center',color:'var(--t-8b7355)'}}>
      <div className="route-spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
);



// Initialize Data Layer on app load
initDataLayer();

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    // Push page_view event to Data Layer for Axon Pixel
    // Use immediate: true since GTM can catch events pushed before it loads
    pushEvent({
      'event': 'page_view',
      'page_path': location.pathname,
      'page_title': document.title
    }, true);

    // Track page view with UTM persistence via our analytics service
    trackPageView();

    // Stash ?ref= from an affiliate link so it survives until registration,
    // which usually happens on a later page than the click.
    captureAffiliateRef(process.env.REACT_APP_API_URL || 'https://api.shakespeareai.net/api');

  }, [location]);

  return null;
}


function App() {
  return (
    <AuthProvider>
      <Router>
        <PageViewTracker />
        {/* Outside <Routes> on purpose: a book takes up to half an hour and
            book_generations has no row until it finishes, so leaving the
            writer used to mean the app went completely silent about it. This
            follows the user to every page. */}
        <GenerationBanner />
        <Suspense fallback={null}>
          <AuthPromotions />
        </Suspense>
        <div className="App">
          <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/account" element={<Account />} />
            <Route path="/subscription" element={<Subscription />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/push" element={<AdminPush />} />
            <Route path="/my-books" element={<MyBooks />} />
            <Route path="/book/:bookId" element={<BookEditor />} />
            <Route path="/read/:shareToken" element={<PublicBookReader />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/check-email" element={<CheckEmail />} />
            <Route path="/library" element={<Library />} />
            {/* SEO from the route, not inside the component: Affiliate has a
                loading branch, a signed-out pitch and a signed-in dashboard, and
                anonymous crawlers only ever see the middle one. */}
            <Route path="/affiliate" element={<><SEO title="Affiliate Program — ShakespeareAI" description="Earn 30% in the first year from each referral’s first paid subscription, then 5% recurring for their lifetime as a paying customer. Free to join." /><Affiliate /></>} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/referral" element={<ReferralPage />} />
            <Route path="/start" element={<Homepage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
              <Route path="/offer" element={<OfferPage />} />
            <Route path="/humanize" element={<AIHumanizer />} />
            <Route path="/translate" element={<BookTranslator />} />
            <Route path="/translate/compare/:bookId" element={<TranslateCompare />} />
            <Route path="/bookwriter" element={<ChapterBuilder />} />
            <Route path="/create-book" element={<BookStart />} />
            <Route path="/book-signal" element={<><SEO title="Book Signal — what is selling on Amazon KDP right now" description="Live Amazon KDP category data: which genres are moving, how crowded each one is, and what a book needs to rank." /><BookSignal /></>} />
            <Route path="/book-signal/:categoryId" element={<BookSignal />} />
            <Route path="/voice-engine" element={<Navigate to="/bookwriter" replace />} />
            <Route path="/chapter-builder" element={<Navigate to="/bookwriter" replace />} />
            <Route path="/generator" element={<BookGenerator />} />
            <Route path="/write/:slug" element={<DynamicLandingPage />} />
            <Route path="/" element={<Homepage />} />
            <Route path="/features" element={<Features />} />
            {/* /about used to fall through to the catch-all and render the
                homepage, serving duplicate content under a second URL. */}
            <Route path="/about" element={<About />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Suspense>
        </div>
        <Suspense fallback={null}>
          <SupportContact />
          <CookieConsent />
          <EmailVerifyBanner />
          <PushSubscribeBanner />
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
